import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Shield } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { supabase } from '../lib/supabase';
import { DarkModeConsentModal } from './DarkModeConsentModal';
import { OneTrustIcon } from './OneTrustIcon';
import { useTheme } from '../lib/ThemeContext';

interface MenuItemsProps {
  isMobile?: boolean;
  onClose: () => void;
  onLogout: () => void;
  user: any;
  userRole: string | null;
  onThemeModalChange: (isOpen: boolean) => void;
}

function MenuItems({ isMobile = false, onClose, onLogout, user, userRole, onThemeModalChange }: MenuItemsProps) {
  return (
    <div className={`${isMobile ? 'space-y-2' : 'space-y-1'}`}>
      {/* Admin Section */}
      {userRole === 'admin' && (
        <div className="py-2 border-b border-gray-200 dark:border-gray-700">
          <Link 
            to="/admin/feature-requests" 
            className="flex items-center w-full text-[#5e9bb8] dark:text-[#5e9bb8] hover:bg-[#5e9bb8]/5 dark:hover:bg-[#5e9bb8]/10 px-4 py-2 rounded-md transition-colors duration-200"
            onClick={onClose}
          >
            <LayoutDashboard className="h-5 w-5 mr-2" />
            Admin Dashboard
          </Link>
        </div>
      )}

      <Link 
        to="/" 
        className="flex items-center w-full text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-md transition-colors duration-200"
        onClick={onClose}
      >
        Home
      </Link>
      <Link 
        to="/resources" 
        className="flex items-center w-full text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-md transition-colors duration-200"
        onClick={onClose}
      >
        Resources
      </Link>
      {user && (userRole === 'employee' || userRole === 'admin') && (
        <Link 
          to="/feature-request" 
          className="flex items-center w-full text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-md transition-colors duration-200"
          onClick={onClose}
        >
          Request a Feature
        </Link>
      )}
      {user && (
        <>
          <Link 
            to="/account" 
            className="flex items-center w-full text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-md transition-colors duration-200"
            onClick={onClose}
          >
            Account Settings
          </Link>
          <button
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="flex items-center w-full text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-md transition-colors duration-200"
          >
            Sign Out
          </button>
        </>
      )}
      {!user && (
        <Link 
          to="/login" 
          className={`
            flex items-center w-full px-4 py-2 rounded-md transition-colors duration-200
            ${isMobile 
              ? 'text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800' 
              : 'bg-[#5e9bb8] text-white hover:bg-[#4d8299] dark:bg-[#5e9bb8] dark:hover:bg-[#4d8299]'
            }
          `}
          onClick={onClose}
        >
          Sign In
        </Link>
      )}
      <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
      <div className="flex items-center justify-between px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors duration-200">
        <div className="flex items-center">
          <ThemeToggle />
        </div>
        <button
          onClick={() => onThemeModalChange(true)}
          className="relative group ml-2"
          aria-label="Learn more about dark mode consent"
        >
          <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
          <OneTrustIcon size="md" />
        </button>
      </div>
      <Link 
        to="/privacy-center" 
        className="flex items-center w-full text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded-md transition-colors duration-200"
        onClick={onClose}
      >
        <Shield className="h-5 w-5 mr-2" />
        Privacy Center
      </Link>
    </div>
  );
}

export function Navigation({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const { theme } = useTheme();

  const logoUrl = "https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/OT%20GUY%20LOGO%20PIC/The%20OT%20guy%20with%20logo%20no%20background.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9PVCBHVVkgTE9HTyBQSUMvVGhlIE9UIGd1eSB3aXRoIGxvZ28gbm8gYmFja2dyb3VuZC5wbmciLCJpYXQiOjE3NDQ5MDIxMDQsImV4cCI6MjAyODcyNjEwNH0.uixRP3etZKu44nBPg5dVpHoPPiuYsRU0_fKHpAFunPo";

  useEffect(() => {
    async function fetchUserRole() {
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setUserRole(profile.role);
        }
      }
    }
    fetchUserRole();
  }, [user]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';

      const handleOutsideClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const sidebar = document.getElementById('mobile-sidebar');
        const menuButton = document.getElementById('menu-button');

        // Only close the menu if clicking outside AND the theme modal is not open
        if (!isThemeModalOpen && sidebar && !sidebar.contains(target) && menuButton && !menuButton.contains(target)) {
          setIsMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.body.style.overflow = '';
      };
    }
  }, [isMenuOpen, isThemeModalOpen]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = '/';
  };

  return (
    <>
      <nav className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg relative z-50 transition-all duration-300">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-24">
            {/* Logo */}
            <div className="relative flex-shrink-0">
              <a href="/" onClick={handleLogoClick} className="block relative z-10">
                <img 
                  src={logoUrl}
                  alt="The OneTrust Guy"
                  className="h-20 w-auto"
                />
              </a>
            </div>

            {/* Spacer */}
            <div className="flex-grow"></div>

            {/* Mobile Menu Button */}
            <div className="flex items-center">
              <button
                id="menu-button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 dark:text-gray-300 hover:text-[#5e9bb8] dark:hover:text-[#5e9bb8] transition-colors duration-200"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu overlay */}
        <div 
          className={`
            fixed inset-0 bg-gray-800 bg-opacity-50 dark:bg-black dark:bg-opacity-50 z-40
            transition-opacity duration-300 ease-in-out
            ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          onClick={() => !isThemeModalOpen && setIsMenuOpen(false)}
        />

        {/* Mobile menu panel */}
        <div
          id="mobile-sidebar"
          className={`
            fixed top-24 right-0 h-[calc(100vh-6rem)] w-64 bg-white dark:bg-gray-800 backdrop-blur-sm shadow-xl overflow-y-auto
            transform transition-transform duration-300 ease-in-out z-50
            ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}
          `}
          style={{
            willChange: 'transform',
            backfaceVisibility: 'hidden'
          }}
        >
          <div className="p-4">
            <MenuItems 
              isMobile={true} 
              onClose={() => setIsMenuOpen(false)} 
              onLogout={onLogout}
              user={user}
              userRole={userRole}
              onThemeModalChange={setIsThemeModalOpen}
            />
          </div>
        </div>
      </nav>

      {/* Dark Mode Modal - Now at the root level */}
      <DarkModeConsentModal
        isOpen={isThemeModalOpen}
        onClose={() => setIsThemeModalOpen(false)}
      />
    </>
  );
}